'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { trackEvent } from '@/lib/analytics';

const trustItems = [
  {
    label: 'Fast & Reliable Funding',
    body: 'From completed application to wire in escrow.',
  },
  {
    label: '1-Page Application',
    body: 'No tax returns, no personal financial statements.',
  },
  {
    label: 'Deal-by-Deal Basis',
    body: 'No long-term commitments or subscription fees.',
  },
  {
    label: 'Refundable Deposits. High-Value Deals.',
    body: 'Commercial real estate, multifamily, 1031 exchanges, oil & gas, business acquisitions, and luxury real estate investments.',
  },
];

export default function HomeCTA() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const els = ref.current?.querySelectorAll('[data-cta-reveal]');
          els?.forEach((el, i) => {
            const element = el as HTMLElement;
            setTimeout(() => {
              element.style.opacity = '1';
              element.style.transform = 'translateY(0)';
              element.style.filter = 'blur(0)';
            }, i * 100);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="cta"
      className="relative z-10 overflow-hidden focus-light"
      style={{ background: 'var(--color-surface)' }}
      aria-label="Call to action for EMD application"
    >
      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 z-0 opacity-[0.5]"
        style={{
          backgroundImage: 'radial-gradient(rgba(27,67,50,0.05) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Top rule — subtle separator from testimonial section */}
      <div
        className="absolute top-0 left-0 right-0 z-10 h-px"
        style={{ background: 'rgba(27,67,50,0.07)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">

          {/* Left */}
          <div>
            <div
              data-cta-reveal
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                filter: 'blur(4px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              {/* Eyebrow */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-5 h-px" style={{ background: 'rgba(201,168,76,0.5)' }} />
                <span
                  className="text-[9px] font-bold uppercase tracking-[0.4em]"
                  style={{ color: 'var(--color-primary)', opacity: 0.75 }}
                >
                  Ready to Close Faster?
                </span>
              </div>

              {/* Headline */}
              <h2
                id="cta-heading"
                className="font-display mb-7"
                style={{
                  fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.025em',
                  lineHeight: 1.05,
                  fontWeight: 300,
                }}
              >
                Your next deal doesn&apos;t have to wait{' '}
                <span style={{ fontWeight: 600, color: 'var(--color-foreground)' }}>for capital to free up.</span>
              </h2>

              <p
                className="text-sm leading-relaxed mb-10"
                style={{
                  color: 'var(--color-muted)',
                  maxWidth: '44ch',
                  borderLeft: '2px solid rgba(27,67,50,0.2)',
                  paddingLeft: '16px',
                }}
              >
                Submit your deal details and our team will get back to you with a funding decision — typically within 48–72 hours.
              </p>

              {/* CTA buttons */}
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  href="/contact"
                  className="btn-primary inline-flex items-center text-[10px] font-bold uppercase tracking-[0.25em] px-8 py-4 transition-all duration-300"
                  style={{ minHeight: '48px', borderRadius: '6px' }}
                  onClick={() => trackEvent('button_click', { button_id: 'cta_submit_deal', location: 'home_cta' })}
                >
                  Submit a Deal
                </Link>
                <Link
                  href="/emd-program"
                  className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.25em] px-8 py-4 transition-all duration-300"
                  style={{
                    background: 'transparent',
                    color: 'var(--color-primary)',
                    /* Border darkened from rgba(27,67,50,0.25) → rgba(27,67,50,0.60) for 3:1 control boundary */
                    border: '1px solid rgba(27,67,50,0.60)',
                    borderRadius: '6px',
                    minHeight: '48px',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(27,67,50,0.06)';
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(27,67,50,0.75)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(27,67,50,0.60)';
                  }}
                  onClick={() => trackEvent('button_click', { button_id: 'cta_emd_program', location: 'home_cta' })}
                >
                  See the EMD Program
                </Link>
                <Link
                  href="/pricing"
                  className="text-[10px] font-semibold uppercase tracking-[0.2em] transition-all duration-200"
                  style={{
                    color: 'var(--color-primary)',
                    opacity: 0.7,
                    borderBottom: '1px solid rgba(27,67,50,0.28)',
                    paddingBottom: '2px',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.7'; }}
                  onClick={() => trackEvent('button_click', { button_id: 'cta_view_pricing', location: 'home_cta' })}
                >
                  View Pricing →
                </Link>
              </div>
            </div>
          </div>

          {/* Right: Trust signals */}
          <div
            className="flex flex-col justify-center"
            style={{
              borderLeft: '1px solid rgba(27,67,50,0.1)',
              paddingLeft: '0',
            }}
          >
            <div
              className="lg:pl-12"
              style={{ borderTop: '1px solid rgba(27,67,50,0.08)' }}
            >
              {trustItems.map((item, i) => (
                <div
                  key={item.label}
                  data-cta-reveal
                  className="flex items-start gap-5 py-5"
                  style={{
                    opacity: 0,
                    transform: 'translateY(16px)',
                    filter: 'blur(4px)',
                    transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
                    borderBottom: i < trustItems.length - 1 ? '1px solid rgba(27,67,50,0.07)' : 'none',
                  }}
                >
                  {/* Number marker */}
                  <span
                    className="font-display font-bold flex-shrink-0 leading-none"
                    style={{
                      fontSize: '1.5rem',
                      color: 'rgba(27,67,50,0.15)',
                      letterSpacing: '-0.03em',
                      marginTop: '2px',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <p
                      className="text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5"
                      style={{ color: 'var(--color-foreground)' }}
                    >
                      {item.label}
                    </p>
                    <p
                      className="text-xs leading-relaxed"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      {item.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
